import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { OPEN_EVENT, applyConsent, getConsent, hasGPC, saveConsent } from "../lib/consent";

export default function CookieBanner() {
  const [banner, setBanner] = useState(false);
  const [settings, setSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const gpc = hasGPC();

  useEffect(() => {
    const existing = getConsent();
    if (existing) applyConsent(existing);
    else { applyConsent(null); setBanner(true); }
    const onOpen = () => { setAnalytics(getConsent()?.analytics ?? false); setSettings(true); };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const decide = (value) => { saveConsent(value); setBanner(false); setSettings(false); };

  return (
    <>
      {banner && (
        <div role="dialog" aria-label="Cookie consent" data-testid="cookie-banner"
          className="fixed z-[90] bottom-4 left-4 right-4 sm:right-auto sm:max-w-md rounded-2xl border border-white/10 bg-ink-2/95 backdrop-blur-xl text-white shadow-2xl shadow-black/40 p-5 sm:p-6 animate-in slide-in-from-bottom-4 fade-in duration-500">
          <div className="flex items-start gap-3">
            <span className="h-9 w-9 shrink-0 rounded-xl bg-neon/15 flex items-center justify-center"><Cookie className="h-4 w-4 text-neon" /></span>
            <div>
              <p className="font-display font-bold text-base">Your privacy, your choice.</p>
              <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">
                We use strictly necessary cookies to run the portal, and — only with your consent — analytics cookies to understand how the site is used.
                {gpc && " We detected a Global Privacy Control signal, so analytics stay off unless you accept."}{" "}
                See our <Link to="/cookies" className="text-neon hover:underline">Cookie Policy</Link> and <Link to="/privacy" className="text-neon hover:underline">Privacy Policy</Link>.
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={() => decide(true)} data-testid="cookie-accept-btn"
              className="rounded-full bg-neon px-5 py-2.5 text-xs font-bold text-ink transition-transform duration-300 hover:scale-105">Accept all</button>
            <button onClick={() => decide(false)} data-testid="cookie-reject-btn"
              className="rounded-full border border-white/20 px-5 py-2.5 text-xs font-bold text-white transition-colors hover:border-neon hover:text-neon">Reject non-essential</button>
            <button onClick={() => { setAnalytics(false); setSettings(true); }} data-testid="cookie-manage-btn"
              className="px-3 py-2.5 text-xs font-semibold text-slate-400 hover:text-white transition-colors">Manage</button>
          </div>
        </div>
      )}

      <Dialog open={settings} onOpenChange={setSettings}>
        <DialogContent className="bg-ink-2 border-white/10 text-white sm:max-w-lg" data-testid="cookie-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Cookie preferences</DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Choose which cookies OrthoPlaniX may use. You can change this at any time from “Cookie settings” in the footer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <PrefRow title="Strictly necessary" desc="Login session, security and consent storage. Required for the portal to work — cannot be switched off." checked disabled testId="cookie-pref-necessary" />
            <PrefRow title="Analytics & performance" desc="Anonymous usage statistics and session diagnostics (PostHog) that help us improve the site. Off until you opt in." checked={analytics} onChange={setAnalytics} testId="cookie-pref-analytics" />
          </div>
          <div className="mt-6 flex flex-wrap justify-end gap-2">
            <button onClick={() => decide(false)} data-testid="cookie-settings-reject-btn" className="rounded-full border border-white/20 px-5 py-2.5 text-xs font-bold hover:border-neon hover:text-neon transition-colors">Reject non-essential</button>
            <button onClick={() => decide(analytics)} data-testid="cookie-settings-save-btn" className="rounded-full bg-neon px-5 py-2.5 text-xs font-bold text-ink hover:scale-105 transition-transform">Save preferences</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PrefRow({ title, desc, checked, onChange, disabled, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-white/5 p-4">
      <div>
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-slate-400 mt-1 leading-relaxed">{desc}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} data-testid={testId} aria-label={title} className="data-[state=checked]:bg-neon mt-0.5" />
    </div>
  );
}
